import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppKit } from "@reown/appkit/react";
import { useAccount, useSignMessage } from "wagmi";
import { Loader2, ShieldAlert, Wallet } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { Alert, AlertDescription } from "@primitive/alert";
import { Button } from "@primitive/button";
import { Toaster } from "@primitive/sonner";
import logoImage from "figma:asset/logo.png";
import { useAuthStore } from "@stores/useAuthStore";
import {
  claimCommunityInviteByToken,
  fetchCommunityInvitePreview,
} from "@lib/apiClients";
import { buildCommunityAdminSectionPath } from "@lib/routing";
import { queryClient, queryKeys } from "@lib/queryClient";
import { readCommunityInviteTokenFromPath } from "@hooks/useHandleInviteToken";

type InvitePreview = Awaited<ReturnType<typeof fetchCommunityInvitePreview>>;
type PageStatus = "loading" | "ready" | "error";

function formatExpiry(expiresAt: string | null) {
  if (!expiresAt) return "No expiry provided";
  return new Date(expiresAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function CommunityInviteAcceptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = useMemo(
    () => readCommunityInviteTokenFromPath(location.pathname),
    [location.pathname],
  );
  const [status, setStatus] = useState<PageStatus>("loading");
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [message, setMessage] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);
  const [isAuthenticatingWallet, setIsAuthenticatingWallet] = useState(false);
  const [shouldAuthenticateAfterConnect, setShouldAuthenticateAfterConnect] =
    useState(false);
  const [shouldClaimAfterAuth, setShouldClaimAfterAuth] = useState(false);
  const walletAuthInFlightRef = useRef(false);
  const inviteClaimInFlightRef = useRef(false);
  const { open: openAppKit } = useAppKit();
  const { address, isConnected } = useAccount();
  const { signMessageAsync, isPending: isSigning } = useSignMessage();
  const {
    isAuthenticated,
    isAuthReady,
    connectWithWallet,
    refreshUserProfile,
    logout,
  } = useAuthStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      isAuthReady: state.isAuthReady,
      connectWithWallet: state.connectWithWallet,
      refreshUserProfile: state.refreshUserProfile,
      logout: state.logout,
    })),
  );

  useEffect(() => {
    let isActive = true;

    if (!inviteToken) {
      setStatus("error");
      setMessage("This invite link is missing its token.");
      return;
    }

    setStatus("loading");
    fetchCommunityInvitePreview(inviteToken)
      .then((nextPreview) => {
        if (!isActive) return;
        setPreview(nextPreview);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        console.error("Failed to load community invite", error);
        setMessage(
          error instanceof Error
            ? error.message
            : "This invite link is invalid or unavailable.",
        );
        setStatus("error");
      });

    return () => {
      isActive = false;
    };
  }, [inviteToken]);

  const handleWalletAuth = useCallback(async () => {
    if (walletAuthInFlightRef.current) return;

    if (!isConnected || !address) {
      setShouldAuthenticateAfterConnect(true);
      await openAppKit();
      return;
    }

    walletAuthInFlightRef.current = true;
    setIsAuthenticatingWallet(true);
    try {
      await connectWithWallet({
        address,
        signMessage: (messageToSign) =>
          signMessageAsync({
            account: address as `0x${string}`,
            message: messageToSign,
          }),
      });
      setShouldAuthenticateAfterConnect(false);
    } catch (error) {
      console.error("Failed to authenticate wallet for invite", error);
      toast.error("Wallet authentication failed", {
        description: "Check your wallet and try again.",
      });
      setShouldAuthenticateAfterConnect(false);
      setShouldClaimAfterAuth(false);
    } finally {
      walletAuthInFlightRef.current = false;
      setIsAuthenticatingWallet(false);
    }
  }, [address, connectWithWallet, isConnected, openAppKit, signMessageAsync]);

  useEffect(() => {
    if (
      !shouldAuthenticateAfterConnect ||
      !isAuthReady ||
      isAuthenticated ||
      !isConnected ||
      !address
    ) {
      return;
    }

    void handleWalletAuth();
  }, [
    address,
    handleWalletAuth,
    isAuthReady,
    isAuthenticated,
    isConnected,
    shouldAuthenticateAfterConnect,
  ]);

  const claimInvite = useCallback(async () => {
    if (!inviteToken || !preview || inviteClaimInFlightRef.current) return;

    inviteClaimInFlightRef.current = true;
    setIsAccepting(true);
    try {
      const result = await claimCommunityInviteByToken(undefined, {
        inviteToken,
      });

      queryClient.removeQueries({
        queryKey: queryKeys.privateCommunities(),
        exact: true,
      });
      queryClient.removeQueries({
        queryKey: queryKeys.theCommunity(result.communityId),
        exact: true,
      });
      queryClient.removeQueries({
        queryKey: queryKeys.privateCampaigns(result.communityId),
        exact: true,
      });

      await Promise.all([
        refreshUserProfile(),
        queryClient.invalidateQueries({
          queryKey: queryKeys.privateCommunities(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.theCommunity(result.communityId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.privateCampaigns(result.communityId),
        }),
      ]);

      toast.success(
        result.claimOutcome === "already_claimed"
          ? "Invite already accepted"
          : "Invite accepted",
      );
      navigate(buildCommunityAdminSectionPath(result.communityId, "quests"), {
        replace: true,
      });
    } catch (error) {
      console.error("Failed to accept community invite", error);
      setShouldClaimAfterAuth(false);
      setMessage(
        error instanceof Error
          ? error.message
          : "We could not accept this invite. Please try again.",
      );
    } finally {
      inviteClaimInFlightRef.current = false;
      setIsAccepting(false);
    }
  }, [inviteToken, navigate, preview, refreshUserProfile]);

  useEffect(() => {
    if (!shouldClaimAfterAuth || !isAuthReady || !isAuthenticated) {
      return;
    }

    void claimInvite();
  }, [claimInvite, isAuthReady, isAuthenticated, shouldClaimAfterAuth]);

  const handleAcceptInvite = async () => {
    if (!inviteToken || !preview || isAccepting || isAuthenticatingWallet) return;

    setMessage("");
    setShouldClaimAfterAuth(true);

    if (isAuthenticated) {
      await claimInvite();
      return;
    }

    await handleWalletAuth();
  };

  const renderContent = () => {
    if (status === "loading") {
      return (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading invite...
        </div>
      );
    }

    if (status === "error" || !preview) {
      return (
        <Alert variant="destructive">
          <ShieldAlert className="size-4" />
          <AlertDescription>
            {message || "This invite link is invalid or unavailable."}
          </AlertDescription>
        </Alert>
      );
    }

    const disabled =
      isAccepting ||
      isSigning ||
      isAuthenticatingWallet ||
      preview.expired ||
      !["pending", "claimed"].includes(preview.status);
    const isInviteFlowRunning =
      isAccepting ||
      isSigning ||
      isAuthenticatingWallet ||
      shouldAuthenticateAfterConnect ||
      shouldClaimAfterAuth;

    return (
      <div className="space-y-5">
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="text-xs uppercase text-muted-foreground">
            Community
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {preview.communityName}
          </div>
          <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
            <div>Role: {preview.role}</div>
            <div>Status: {preview.expired ? "expired" : preview.status}</div>
            <div>Expires: {formatExpiry(preview.expiresAt)}</div>
          </div>
        </div>

        {message && (
          <Alert variant="destructive">
            <ShieldAlert className="size-4" />
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
          <div className="font-medium text-foreground">Wallet sign-in required</div>
          <p className="mt-1">
            Gila Quest uses your wallet as your account. Connect a wallet and sign
            a message to accept this admin invite.
          </p>
          <p className="mt-2">
            No gas fee. Signing only proves you control the wallet. If you do not
            have one yet, create one with a trusted wallet app and return to this
            invite link.
          </p>
        </div>

        {!isAuthReady ? (
          <Button className="h-11 w-full" disabled>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Checking session...
          </Button>
        ) : (
          <Button
            type="button"
            className="h-11 w-full"
            onClick={handleAcceptInvite}
            disabled={disabled}
          >
            {isInviteFlowRunning ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Wallet className="mr-2 size-4" />
            )}
            {isInviteFlowRunning
              ? "Accepting invite..."
              : "Accept invite with wallet"}
          </Button>
        )}

        {isAuthenticated && (
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => logout()}
            disabled={isAccepting}
          >
            Use a different wallet
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-10">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={logoImage} alt="gilaquest" className="mb-5 size-16 object-contain" />
          <h1 className="text-2xl font-semibold">Accept community invite</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Connect a wallet and sign a message to join this community team.
          </p>
        </div>
        {renderContent()}
        <Button
          type="button"
          variant="ghost"
          className="mt-6 w-full"
          onClick={() => navigate("/", { replace: true })}
        >
          Back to dashboard
        </Button>
      </main>
    </div>
  );
}
